import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Shield, BadgeCheck, FileText, Home, Check, ArrowRight, Lock, TrendingUp, Handshake, ChevronDown, Star, Phone, Calendar, Award, Clock } from "lucide-react";
import { AnimatedCounter } from "@/components/AnimatedCounter";

const HERO_IMAGE = "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1920&q=80";

const avantages = [
  { icon: Lock, title: "Loyer garanti", desc: "Un loyer fixe versé chaque mois, que le logement soit occupé ou non. Zéro aléa, zéro vacance locative." },
  { icon: Shield, title: "Zéro gestion", desc: "Nous prenons tout en charge : annonces, accueil, ménage, linge, maintenance, communication." },
  { icon: TrendingUp, title: "Bien valorisé", desc: "Votre patrimoine est entretenu et valorisé en permanence grâce à notre gestion professionnelle." },
  { icon: Handshake, title: "Cadre juridique clair", desc: "Bail civil professionnel encadré par le Code civil. Transparent, légal et sécurisé." },
];

const inclusList = [
  "Vous restez propriétaire de votre bien",
  "Happyhouse conciergerie est votre locataire unique",
  "Loyer fixe garanti chaque mois",
  "Zéro gestion de votre côté",
  "Assurance RC professionnelle incluse",
  "Entretien complet du logement",
  "Ameublement et décoration si nécessaire",
  "Rapports mensuels détaillés",
];

const timeline = [
  { icon: Phone, title: "Premier échange", desc: "Appel ou rendez-vous pour comprendre vos objectifs et visiter votre bien.", duration: "Jour 1" },
  { icon: FileText, title: "Proposition & Signature", desc: "Nous vous présentons une offre sur mesure avec le montant du loyer garanti. Signature du bail civil.", duration: "Jour 7" },
  { icon: Home, title: "Préparation du bien", desc: "Shooting photo, ameublement si nécessaire, création des annonces sur les plateformes.", duration: "Jour 14" },
  { icon: Calendar, title: "Premières réservations", desc: "Votre logement est en ligne et les premières réservations arrivent. Vous recevez votre loyer.", duration: "Jour 21" },
  { icon: Award, title: "Gestion continue", desc: "Nous gérons tout au quotidien. Vous recevez un rapport mensuel et votre loyer chaque mois.", duration: "Chaque mois" },
];

const miniFaqs = [
  { q: "Est-ce que la sous-location professionnelle est légale ?", a: "Oui, c'est un bail civil de droit commun encadré par les articles 1709 et suivants du Code civil. C'est un contrat entre un propriétaire et un professionnel, différent du bail d'habitation." },
  { q: "Que se passe-t-il si le logement n'est pas loué certains mois ?", a: "Rien ne change pour vous. Votre loyer est garanti et versé chaque mois, quel que soit le taux d'occupation. C'est notre risque, pas le vôtre." },
  { q: "Puis-je récupérer mon bien quand je le souhaite ?", a: "Le contrat prévoit des conditions de résiliation claires et raisonnables. La durée et les modalités sont discutées ensemble avant la signature." },
  { q: "Qui paie les charges et les travaux ?", a: "Les charges courantes et la petite maintenance sont à notre charge. Les gros travaux structurels restent à la charge du propriétaire, comme dans tout contrat de location." },
  { q: "Mon bien sera-t-il bien entretenu ?", a: "Absolument. Nous avons un protocole de nettoyage en 45 points, un contrôle qualité systématique, et un réseau d'artisans locaux pour toute intervention." },
];

export default function SousLocationPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div data-testid="sous-location-page">
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-end overflow-hidden">
        <img src={HERO_IMAGE} alt="Sous-location professionnelle" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1008]/90 via-[#1a1008]/40 to-[#1a1008]/10" />
        <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 pt-32">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 mb-3">
            <span className="inline-block bg-[#C9922A]/20 text-[#C9922A] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border border-[#C9922A]/30 backdrop-blur-sm">Favori</span>
            <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold">Offre Sérénité</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-3xl">
            Sous-location professionnelle
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-white/60 mt-4 max-w-2xl text-lg">
            Un loyer garanti chaque mois, sans aucune gestion de votre part. La tranquillité absolue pour les propriétaires exigeants.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3 bg-[#C9922A] text-white rounded-full font-semibold hover:bg-[#B8831F] transition-colors" data-testid="sous-location-hero-cta">
              Estimer mon loyer garanti <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:+33767484313" className="inline-flex items-center gap-2 px-7 py-3 border-2 border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-colors">
              <Phone className="w-4 h-4" /> Nous appeler
            </a>
          </motion.div>
        </div>
      </section>

      {/* Stats en chiffres */}
      <section className="py-14 bg-[#004333]" data-testid="sous-location-stats">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: 100, suffix: "%", label: "Loyer garanti", sub: "versé chaque mois" },
              { value: 0, suffix: " EUR", label: "Frais de gestion", sub: "pour le propriétaire" },
              { value: 45, suffix: " pts", label: "Protocole ménage", sub: "qualité hôtelière" },
              { value: 12, suffix: " mois", label: "Durée min.", sub: "renouvelable" },
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="text-3xl md:text-4xl font-black text-white mb-1">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} prefix="" />
                </div>
                <p className="text-[#C9922A] font-semibold text-sm">{stat.label}</p>
                <p className="text-white/40 text-xs">{stat.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-20 bg-[#F5F0EB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">Avantages</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#004333]">Pourquoi choisir l'offre Sérénité ?</h2>
            <p className="text-[#6B5E50] mt-3 max-w-lg mx-auto">La solution idéale pour les propriétaires qui veulent des revenus garantis sans aucune contrainte.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {avantages.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl p-6 border border-[#E0D6CA] shadow-sm hover:shadow-md transition-shadow"
                data-testid={`sous-location-avantage-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#C9922A]/10 border border-[#C9922A]/20 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-[#C9922A]" />
                </div>
                <h3 className="font-display text-lg text-[#004333] mb-2">{item.title}</h3>
                <p className="text-[#6B5E50] text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline - Processus */}
      <section className="py-20 bg-[#EDE8E1]" data-testid="sous-location-timeline">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">Processus</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#004333]">De la signature au premier loyer</h2>
            <p className="text-[#6B5E50] mt-3 max-w-lg mx-auto">Un processus rapide et transparent. En 3 semaines, tout est en place.</p>
          </div>
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-[#C9922A]/40 via-[#C9922A]/20 to-transparent hidden md:block" />
            <div className="space-y-8">
              {timeline.map((step, i) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex gap-6 items-start"
                  >
                    <div className="relative z-10 w-16 h-16 rounded-2xl bg-white border border-[#E0D6CA] flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Icon className="w-6 h-6 text-[#C9922A]" />
                    </div>
                    <div className="flex-1 bg-white rounded-xl p-5 border border-[#E0D6CA] shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-[#004333]">{step.title}</h3>
                        <span className="flex items-center gap-1 text-xs text-[#C9922A] font-medium bg-[#C9922A]/10 px-2.5 py-1 rounded-full">
                          <Clock className="w-3 h-3" /> {step.duration}
                        </span>
                      </div>
                      <p className="text-[#6B5E50] text-sm leading-relaxed">{step.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Comment ça marche - Bail civil */}
      <section className="py-20 bg-[#1A120B]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3 text-white">
              <span className="inline-block bg-[#C9922A]/10 text-[#C9922A] px-4 py-1.5 rounded-full text-sm font-semibold mb-5 border border-[#C9922A]/20">Le bail civil professionnel</span>
              <h2 className="font-display text-2xl md:text-3xl mb-5">Comment ça fonctionne ?</h2>
              <p className="text-white/60 leading-relaxed mb-6">
                La sous-location professionnelle est encadrée par le <strong className="text-white/80">Code civil</strong> (articles 1709 et suivants).
                Happyhouse conciergerie signe un bail civil avec vous et devient le locataire unique de votre bien.
                C'est un contrat de droit commun, différent du bail d'habitation classique.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Shield, title: "Durée flexible", desc: "Négociée entre les parties, généralement 1 à 3 ans" },
                  { icon: BadgeCheck, title: "Loyer garanti", desc: "Un loyer fixe versé chaque mois, sans exception" },
                  { icon: FileText, title: "100% légal", desc: "Encadré par le Code civil, aucune autorisation nécessaire" },
                  { icon: Home, title: "Bien préservé", desc: "Votre bien est entretenu et valorisé en permanence" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.05] border border-white/10">
                    <div className="w-9 h-9 rounded-lg bg-[#C9922A]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-[#C9922A]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{item.title}</p>
                      <p className="text-xs text-white/50 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="bg-[#004333] rounded-2xl p-6 text-white">
                <h3 className="font-display text-lg mb-4">Ce qui est inclus</h3>
                <ul className="space-y-3 text-sm">
                  {inclusList.map((point, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-[#C9922A] flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Témoignage */}
      <section className="py-20 bg-[#F5F0EB]" data-testid="sous-location-testimonial">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-[#C9922A] text-[#C9922A]" />)}
          </div>
          <blockquote className="text-xl md:text-2xl text-[#004333] italic leading-relaxed mb-8">
            "Je cherchais une solution sans stress pour mon appartement à Limoges. Avec l'offre Sérénité, je reçois mon loyer le 5 de chaque mois, comme une horloge. Je ne me suis jamais senti aussi serein."
          </blockquote>
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 bg-[#C9922A]/15 rounded-full flex items-center justify-center text-[#C9922A] font-semibold">P</div>
            <div className="text-left">
              <div className="font-semibold text-[#004333]">Pierre L.</div>
              <div className="text-xs text-[#8B7D6B]">Propriétaire à Limoges — Offre Sérénité depuis 18 mois</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="py-20 bg-[#EDE8E1]" data-testid="sous-location-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">FAQ</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#004333]">Questions sur l'offre Sérénité</h2>
          </div>
          <div className="space-y-3">
            {miniFaqs.map((faq, i) => (
              <div key={i} className="border border-[#E0D6CA] bg-[#FFFDF9] rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-[#EDE8E1]/50 transition-colors"
                  data-testid={`sous-location-faq-${i}`}
                >
                  <span className="font-semibold text-[#004333] pr-4 text-sm">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#C9922A] flex-shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                      <div className="px-5 pb-5 text-[#6B5E50] text-sm leading-relaxed border-t border-[#E8E0D4] pt-4">{faq.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#004333]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-2xl md:text-3xl text-white mb-4">Intéressé par l'offre Sérénité ?</h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Contactez-nous pour une estimation gratuite et sans engagement de votre bien.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#C9922A] text-white rounded-full font-semibold hover:bg-[#B8831F] transition-colors" data-testid="sous-location-cta">
              Estimer mon bien <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/simulateur" className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-colors">
              Simuler mes revenus
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
